import React, { useEffect, useState } from "react";
import { Dropdown, Button, Form } from "react-bootstrap";
import type { Column } from "@tanstack/react-table";
import { MecordColors } from "Constants/Color";

type VisibilityColumnInstance<TData> = Column<TData, unknown> & {
  getIsVisible: () => boolean;
  toggleVisibility: (value: boolean) => void;
};

interface Props<TData> {
  /** Column instances from react-table (table.getAllLeafColumns()) */
  columns?: Column<TData, unknown>[];
  /** Title on the toggle button */
  title?: string;
  /** Column ids to exclude from UI (e.g. action column) */
  excludedColumns?: string[];
  /** Optional className for the outer Dropdown */
  className?: string;
}

/**
 * ColumnVisibilityDropdown
 *
 * Usage: pass table.getAllLeafColumns()
 * <ColumnVisibilityDropdown columns={table.getAllLeafColumns()} excludedColumns={['action']} />
 */
export default function ColumnVisibilityDropdown<TData>({
  columns = [],
  excludedColumns = [],
  className,
}: Props<TData>): JSX.Element {
  const themeColor = MecordColors?.ThemeBGColor;

  // Filter out excluded columns
  const visibilityColumns = columns as VisibilityColumnInstance<TData>[];
  // Maintain local state for column visibility
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [toggle, setToggle] = useState(true);

  // Initialize column visibility state
  React.useEffect(() => {
    const initialVisibility: Record<string, boolean> = {};
    visibilityColumns.forEach((col) => {
      initialVisibility[col.id] = col.getIsVisible();
    });
    setColumnVisibility(initialVisibility);
  }, [visibilityColumns]);

  const toggleOne = (col: any) => {
    const currentVisibility = columnVisibility[col.id] ?? col.getIsVisible();
    const newVisibility = !currentVisibility;

    // Update table state
    col.toggleVisibility(newVisibility);

    // Update local state immediately
    setColumnVisibility((prev) => ({
      ...prev,
      [col.id]: newVisibility,
    }));

    setToggle((prev) => {
      return !prev;
    });
  };

  const showAll = () => {
    visibilityColumns.forEach((c) => c.toggleVisibility(true));
    const newVisibility: Record<string, boolean> = {};
    visibilityColumns.forEach((col) => {
      newVisibility[col.id] = true;
    });
    setColumnVisibility(newVisibility);
    setToggle((prev) => !prev);
  };

  const hideAll = () => {
    visibilityColumns.forEach((c) => c.toggleVisibility(false));
    const newVisibility: Record<string, boolean> = {};
    visibilityColumns.forEach((col) => {
      newVisibility[col.id] = false;
    });
    setColumnVisibility(newVisibility);
    setToggle((prev) => !prev);
  };
  useEffect(() => {}, [toggle]);

  const toggleButtonStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderColor: themeColor,
    color: themeColor,
    borderRadius: "4px",
    padding: "4px 12px",
    height: "32px",
    minHeight: "32px",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${themeColor}`,
  };

  return (
    <Dropdown className={className} align="end" autoClose="outside">
      <style>
        {`
          .custom-column-visibility-button {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            border-radius: 4px !important;
            padding: 4px 12px !important;
            height: 32px !important;
            min-height: 32px !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            border: 1px solid ${themeColor} !important;
          }
          .custom-column-visibility-button:hover:not(:disabled) {
            border-color: ${themeColor} !important;
            opacity: 1 !important;
          }
          .custom-column-visibility-button:focus {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            box-shadow: 0 0 0 0.2rem ${themeColor}40 !important;
          }
          .form-check-input:checked {
            background-color: ${themeColor} !important;
            border-color: ${themeColor} !important;
          }
          .form-check-input:focus {
            border-color: ${themeColor} !important;
            box-shadow: 0 0 0 0.25rem ${themeColor}40 !important;
          }
        `}
      </style>
      <Dropdown.Toggle
        as="button"
        className="custom-column-visibility-button btn-md "
        style={toggleButtonStyle}
      >
        <i className="ph ph-sliders"></i>
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: 240, padding: "0.5rem" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="small text-muted">Show / Hide columns</div>
          <div>
            <Button
              size="sm"
              variant="link"
              onClick={showAll}
              className="p-0 me-2"
            >
              All
            </Button>
            <Button size="sm" variant="link" onClick={hideAll} className="p-0">
              None
            </Button>
          </div>
        </div>

        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          <Form>
            {visibilityColumns.map((col: any) => {
              // header may be string | JSX.Element | function — fall back to id if not string
              const label =
                typeof col.columnDef.header === "string"
                  ? col.columnDef.header
                  : (col.columnDef.header &&
                      (col.columnDef.header as any).toString()) ||
                    col.id;
              const isChecked = columnVisibility[col.id] ?? col.getIsVisible();
              return (
                <Form.Check
                  key={`${col.id}-${toggle}`}
                  type="checkbox"
                  id={`colvis-${col.id}`}
                  label={label}
                  checked={isChecked}
                  // prevent dropdown from closing when clicking (autoClose="outside" already handles it)
                  onChange={() => toggleOne(col)}
                  className="mb-2"
                />
              );
            })}
          </Form>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
